import { SOCIAL_PROOF_STATS } from '#pages/data';
import {
  Box,
  Container,
  Flex,
  Heading,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { motion, useInView } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';

const AnimatedNumber = ({ value, label, icon }: { value: string; label: string; icon: string }) => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  const [displayed, setDisplayed] = useState('0');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const labelColor = useColorModeValue('gray.500', 'gray.400');

  useEffect(() => {
    if (!isInView) return;
    // Extract numeric part — avoid unnecessary escapes inside character classes
    const numMatch = value.match(/([+]?[0-9]+)/);
    if (!numMatch) { setDisplayed(value); return; }
    const num = parseInt(numMatch[1], 10);
    // Reconstruct prefix (non-digit start) and suffix (non-digit end)
    const prefix = value.slice(0, value.search(/[0-9]/));
    const suffix = value.slice(value.search(/[0-9]/) + String(num).length);
    let start = 0;
    const duration = 1800;
    const step = Math.ceil(num / (duration / 16));
    const timer = setInterval(() => {
      start = Math.min(start + step, num);
      setDisplayed(`${prefix}${start}${suffix}`);
      if (start >= num) clearInterval(timer);
    }, 16);
    return () => clearInterval(timer);
  }, [isInView, value]);

  return (
    <Box
      ref={ref}
      as={motion.div}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: '0.5' } as any}
      bg={cardBg}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      px={6}
      py={5}
      textAlign="center"
      boxShadow="sm"
      sx={{ transition: 'all 0.2s' }}
      _hover={{ boxShadow: 'md', transform: 'translateY(-2px)' }}
      minW={{ base: '140px', md: '160px' }}
    >
      <Text fontSize="2xl" mb={1}>{icon}</Text>
      <Heading
        as="p"
        fontSize={{ base: '2xl', md: '3xl' }}
        fontWeight="extrabold"
        color={useColorModeValue('blue.600', 'blue.300')}
        lineHeight="1"
      >
        {displayed}
      </Heading>
      <Text fontSize="xs" color={labelColor} mt={1} fontWeight="medium">
        {label}
      </Text>
    </Box>
  );
};

const SocialProofBar = () => {
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const headingColor = useColorModeValue('gray.700', 'gray.200');

  return (
    <Box bg={bgColor} py={{ base: 10, md: 14 }}>
      <Container maxW="container.xl">
        <Text
          textAlign="center"
          fontSize="sm"
          fontWeight="semibold"
          letterSpacing="widest"
          color={headingColor}
          textTransform="uppercase"
          mb={8}
        >
          Proven Results Across 7+ Industries
        </Text>
        <Flex
          wrap="wrap"
          gap={4}
          justify="center"
          align="stretch"
        >
          {SOCIAL_PROOF_STATS.map((stat) => (
            <AnimatedNumber
              key={stat.label}
              value={stat.value}
              label={stat.label}
              icon={stat.icon}
            />
          ))}
        </Flex>
      </Container>
    </Box>
  );
};

export default SocialProofBar;
